import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, DoorOpen, ShieldCheck, MapPin, Users, Printer, FileText, Plus, Pencil, Trash2, CheckCircle2, LayoutDashboard,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  planned: 'bg-slate-100 text-slate-700 border-slate-300',
  'in-progress': 'bg-blue-50 text-blue-800 border-blue-300',
  completed: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  'needs-follow-up': 'bg-amber-50 text-amber-900 border-amber-300',
  'could-not-complete': 'bg-red-50 text-red-800 border-red-200',
};

const EMPTY = {
  street: '', area: '', route_section: '', assigned_volunteer: '', canvass_date: '', status: 'planned',
  doors_attempted: 0, conversations: 0, materials_left: 0, issue_reports_suggested: 0,
  follow_up_needed: false, follow_up_date: '', notes: '', volunteer_notes: '', materials_used: [], qr_source: 'flyer',
};

const EntryDialog = ({ open, entry, meta, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(entry ? { ...EMPTY, ...entry } : { ...EMPTY }); }, [entry, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.street.trim()) { toast.error('Street / road name is required'); return; }
    setBusy(true);
    try {
      if (entry?.id) await api.put(`/admin/canvass/${entry.id}`, form);
      else await api.post('/admin/canvass', form);
      toast.success(entry?.id ? 'Canvass entry updated' : 'Canvass entry added');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="canvass-entry-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{entry?.id ? 'Edit Canvass Entry' : 'Add Canvass Entry'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Street / road name *</label>
            <Input value={form.street} onChange={(e) => set('street', e.target.value)} className="h-11" data-testid="canvass-street-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Community / area</label>
              <Input value={form.area} onChange={(e) => set('area', e.target.value)} className="h-11" data-testid="canvass-area-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Route section</label>
              <Input value={form.route_section} onChange={(e) => set('route_section', e.target.value)} placeholder="e.g. north side, #1-45" className="h-11" data-testid="canvass-route-section-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Assigned volunteer</label>
              <Input value={form.assigned_volunteer} onChange={(e) => set('assigned_volunteer', e.target.value)} className="h-11" data-testid="canvass-volunteer-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Canvass date</label>
              <Input type="date" value={form.canvass_date} onChange={(e) => set('canvass_date', e.target.value)} className="h-11" data-testid="canvass-date-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-11" data-testid="canvass-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">QR source used</label>
              <Select value={form.qr_source || 'flyer'} onValueChange={(v) => set('qr_source', v)}>
                <SelectTrigger className="h-11" data-testid="canvass-qr-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.qr_sources).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[['doors_attempted', 'Doors'], ['conversations', 'Conversations'], ['materials_left', 'Materials left'], ['issue_reports_suggested', 'Reports suggested']].map(([k, l]) => (
              <div key={k}>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">{l}</label>
                <Input type="number" min="0" value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11" data-testid={`canvass-${k.replace(/_/g, '-')}-input`} />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Materials used</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(meta.materials).map(([k, l]) => (
                <button key={k} type="button" onClick={() => set('materials_used', form.materials_used.includes(k) ? form.materials_used.filter((m) => m !== k) : [...form.materials_used, k])}
                  className={`px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-colors ${form.materials_used.includes(k) ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-600 border-slate-300'}`}
                  data-testid={`canvass-material-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.follow_up_needed} onChange={(e) => set('follow_up_needed', e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#1a2744]" data-testid="canvass-followup-checkbox" />
            Follow-up needed
          </label>
          {form.follow_up_needed && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Follow-up date</label>
              <Input type="date" value={form.follow_up_date} onChange={(e) => set('follow_up_date', e.target.value)} className="h-11" data-testid="canvass-followup-date-input" />
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Private notes (admin-only — no resident names or contact details)</label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} data-testid="canvass-notes-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Volunteer notes</label>
            <Textarea value={form.volunteer_notes} onChange={(e) => set('volunteer_notes', e.target.value)} rows={2} data-testid="canvass-volunteer-notes-input" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="canvass-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save Entry
            </Button>
            <Button onClick={onClose} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RouteSheetPanel = ({ meta }) => {
  const [filters, setFilters] = useState({ date: '', area: '', volunteer: '', status: 'all', follow_up_needed: false });
  const [includeNotes, setIncludeNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const body = { include_private_notes: includeNotes };
      if (filters.date) body.date = filters.date;
      if (filters.area) body.area = filters.area;
      if (filters.volunteer) body.volunteer = filters.volunteer;
      if (filters.status !== 'all') body.status = filters.status;
      if (filters.follow_up_needed) body.follow_up_needed = true;
      const r = await api.post('/admin/canvass/route-sheet', body);
      setResult(r.data);
      toast.success(`Route sheet ready — ${r.data.entries} entr${r.data.entries === 1 ? 'y' : 'ies'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Route sheet generation failed'); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 mb-5" data-testid="canvass-route-sheet-panel">
      <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2 mb-2"><Printer className="h-4 w-4 text-[#C9A84C]" /> Canvass Route Sheet</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Input type="date" value={filters.date} onChange={(e) => set('date', e.target.value)} className="h-10 text-sm" data-testid="route-filter-date" />
        <Input placeholder="Community / area" value={filters.area} onChange={(e) => set('area', e.target.value)} className="h-10 text-sm" data-testid="route-filter-area" />
        <Input placeholder="Volunteer" value={filters.volunteer} onChange={(e) => set('volunteer', e.target.value)} className="h-10 text-sm" data-testid="route-filter-volunteer" />
        <Select value={filters.status} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="h-10 text-sm" data-testid="route-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(meta.statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 h-10 cursor-pointer">
          <input type="checkbox" checked={filters.follow_up_needed} onChange={(e) => set('follow_up_needed', e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#1a2744]" data-testid="route-filter-followup" />
          Follow-up only
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 mt-3 cursor-pointer" data-testid="route-include-notes-label">
        <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#1a2744]" data-testid="route-include-notes" />
        Include private notes <span className="font-normal text-slate-400">(off by default — keep notes private unless volunteers truly need them)</span>
      </label>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={generate} disabled={busy} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="route-generate-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} Generate Route Sheet
        </Button>
        {result && (
          <Button onClick={() => window.open(fileUrl(result.pdf), '_blank', 'noopener')} variant="outline" className="h-10 font-bold btn-press" data-testid="route-open-pdf-btn">
            <FileText className="h-4 w-4 mr-1.5" /> Open PDF ({result.entries} entries{result.included_private_notes ? ', notes included' : ''})
          </Button>
        )}
      </div>
      {result && <img src={`${fileUrl(result.png)}?t=${result.generated_at}`} alt="Route sheet preview" className="mt-3 rounded border border-slate-200 w-full max-w-md bg-white" data-testid="route-preview-img" />}
    </div>
  );
};

export default function AdminCanvassLog() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const load = useCallback(() => {
    api.get('/admin/canvass').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/canvass/${id}`);
      toast.success('Canvass entry removed');
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="canvass-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Canvass Log.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const items = statusFilter === 'all' ? data.items : data.items.filter((i) => i.status === statusFilter);

  return (
    <div data-testid="admin-canvass-log">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><DoorOpen className="h-6 w-6 text-[#C9A84C]" /> Canvass Log</h1>
          <p className="text-sm text-slate-500 mt-1">Private street-by-street door-knocking tracker — progress only, never resident information.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="canvass-dashboard-btn">
            <Link to="/admin"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard</Link>
          </Button>
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="canvass-poster-drops-btn">
            <Link to="/admin/poster-drops"><MapPin className="h-4 w-4 mr-1.5" /> Poster Drop List</Link>
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="canvass-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Entry
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="canvass-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      <RouteSheetPanel meta={data} />

      <div className="flex gap-2 overflow-x-auto pb-1 mb-3">
        {[['all', `All (${data.items.length})`], ...Object.entries(data.statuses).map(([k, l]) => [k, `${l} (${data.items.filter((i) => i.status === k).length})`])].map(([v, l]) => (
          <Button key={v} onClick={() => setStatusFilter(v)} variant={statusFilter === v ? 'default' : 'outline'} size="sm" className={`h-9 shrink-0 text-xs font-bold btn-press ${statusFilter === v ? 'bg-[#1a2744] text-white' : ''}`} data-testid={`canvass-filter-${v}`}>{l}</Button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="canvass-empty">
          <DoorOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No canvass entries yet</p>
          <p className="text-xs text-slate-500 mt-1">Add the first street to start tracking door-knocking progress.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="canvass-list">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`canvass-entry-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-serif font-bold text-[#1a2744]">{it.street}</p>
                  <p className="text-xs text-slate-500">{it.area || 'No community set'}{it.route_section ? ` · ${it.route_section}` : ''}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] font-bold shrink-0 ${STATUS_STYLES[it.status]}`}>{data.statuses[it.status]}</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {it.assigned_volunteer ? `Volunteer: ${it.assigned_volunteer} · ` : ''}{it.canvass_date ? `Date: ${it.canvass_date} · ` : ''}
                Doors: <span className="font-bold text-[#1a2744]">{it.doors_attempted}</span> · Conversations: {it.conversations} · Left: {it.materials_left}{it.issue_reports_suggested ? ` · Reports suggested: ${it.issue_reports_suggested}` : ''}
              </p>
              {it.follow_up_needed && <Badge variant="outline" className="mt-2 text-[10px] font-bold bg-amber-50 border-amber-300 text-amber-900">Follow-up{it.follow_up_date ? ` by ${it.follow_up_date}` : ' needed'}</Badge>}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setEditing(it); setDialogOpen(true); }} className="h-8 text-xs font-bold" data-testid={`canvass-edit-${it.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(it.id)} className="h-8 text-xs font-bold text-red-500" data-testid={`canvass-delete-${it.id}`}>
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EntryDialog open={dialogOpen} entry={editing} meta={data} onClose={() => setDialogOpen(false)} onSaved={load} />
    </div>
  );
}
